/**
 * Supabase Storage helpers — used by Expenses for receipt files.
 *
 * Receipts are uploaded to the `receipts` bucket at path "{user_id}/{uuid}.{ext}".
 * The `Receipt File` column on each expense row holds the path (not a data URL).
 * RLS policy on the bucket restricts read/write to objects whose first path
 * segment matches the requester's auth.uid().
 *
 * The legacy data: URL format is still tolerated in viewers + the one-time
 * migrateReceiptsToStorage() sweep (which lives next to the Expenses view).
 */
import { sb, CLOUD_USER } from "./supabase";

const RECEIPT_BUCKET = "receipts";

/** True if the string looks like a Storage path (not a data:/blob:/http URL). */
export function isStoragePath(s: unknown): s is string {
  return typeof s === "string"
    && s.length > 0
    && !s.startsWith("data:")
    && !s.startsWith("blob:")
    && !/^https?:\/\//i.test(s);
}

/** Derive a sensible file extension from filename or MIME. */
function extFromName(name: string | undefined, mimeType: string | undefined): string {
  if (name && /\.[A-Za-z0-9]+$/.test(name)) {
    const m = name.match(/\.([A-Za-z0-9]+)$/);
    return m ? m[1].toLowerCase() : "bin";
  }
  if (mimeType === "application/pdf") return "pdf";
  if (mimeType && mimeType.startsWith("image/")) return mimeType.split("/")[1].split(";")[0];
  return "bin";
}

/** Convert a `data:<mime>;base64,<payload>` URL into a Blob. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const m = /^data:([^;,]+)?(;base64)?,(.*)$/i.exec(dataUrl);
  if (!m) throw new Error("Invalid data URL");
  const mime = m[1] || "application/octet-stream";
  const isB64 = !!m[2];
  const payload = m[3];
  if (isB64) {
    const bin = atob(payload);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }
  return new Blob([decodeURIComponent(payload)], { type: mime });
}

/** Upload a Blob to the receipts bucket and return its storage path. */
export async function uploadReceiptBlob(
  blob: Blob,
  originalName: string | undefined,
  mimeType: string | undefined,
): Promise<string> {
  if (!CLOUD_USER) throw new Error("Not signed in");
  const ext = extFromName(originalName, mimeType || blob.type);
  const id = (crypto && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${CLOUD_USER.id}/${id}.${ext}`;
  const { error } = await sb.storage.from(RECEIPT_BUCKET).upload(path, blob, {
    contentType: mimeType || blob.type || "application/octet-stream",
    upsert: false,
  });
  if (error) throw error;
  return path;
}

/** Convenience: convert a data URL → Blob → upload → return path. */
export async function uploadReceiptDataUrl(
  dataUrl: string,
  originalName: string | undefined,
  mimeType: string | undefined,
): Promise<string> {
  const blob = dataUrlToBlob(dataUrl);
  return uploadReceiptBlob(blob, originalName, mimeType || blob.type);
}

/** Get a short-lived signed URL for a stored receipt (good for 1 hour). */
export async function getReceiptSignedUrl(path: string): Promise<string> {
  const { data, error } = await sb.storage.from(RECEIPT_BUCKET).createSignedUrl(path, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
}

/** Delete a stored receipt object. Best-effort — errors are swallowed by the caller's catch. */
export async function deleteReceiptByPath(path: string | undefined | null): Promise<void> {
  if (!path || !isStoragePath(path)) return;
  await sb.storage.from(RECEIPT_BUCKET).remove([path]);
}
